<script lang="ts" setup>
import type { Announcement } from '~/models';

import { useNuxtApp } from '#app';

const route = useRoute();
const cookies = useCookieStore();

const { $device: { isMobile } } = useNuxtApp();
const carouselRef = ref();
let autoplayIntervalId: ReturnType<typeof setInterval> | null = null;

const announcementStore = useAnnouncementStore();

const dismissed = useState<boolean>('announcements:dismissed', () => false);

const allowedRoutes = ['index', 'menu'];
const isAllowedRoute = computed<boolean>(() => allowedRoutes.includes(route.name as string));
const shouldFetch = computed<boolean>(() => {
  return isAllowedRoute.value &&
    !dismissed.value &&
    cookies.isCookieResolved;
});

const { data: announcementsData } = await useAsyncData('announcements-modal', async () => {
  if (!shouldFetch.value) {
    return [];
  }
  return await announcementStore.list();
}, {
  immediate: shouldFetch.value,
  watch: [shouldFetch]
});

const announcements = computed<Announcement[]>(() => announcementsData.value ?? []);
const shouldShow = computed<boolean>(() => shouldFetch.value && announcements.value.length > 0);
const modalWidth = isMobile ? 'w-[92vw]' : 'w-full';
const modalHeight = isMobile ? 'max-h-[90vh]' : 'max-h-[80vh]';
const modalContainer = isMobile ? 'items-center justify-center' : 'items-center';

const getCardStyle = (announcement: Announcement): Record<string, string> => ({
  background: `linear-gradient(180deg, ${announcement.bgColor1}, ${announcement.bgColor2})`,
  color: announcement.fontColor
});

const carouselUi = {
  container: 'gap-0',
  item: 'basis-full snap-always',
  indicators: {
    wrapper: 'relative bottom-0 mt-4'
  }
};

const closeInterval = (): void => {
  if (!autoplayIntervalId) {
    return;
  }

  clearInterval(autoplayIntervalId);
  autoplayIntervalId = null;
};


const close = (): void => {
  dismissed.value = true;
  announcementsData.value = [];
  closeInterval();
};

onMounted(() => {
  autoplayIntervalId = setInterval(() => {
    if (!carouselRef.value || shouldShow.value === false || announcements.value.length <= 1) {
      return;
    }

    if (carouselRef.value.page === carouselRef.value.pages) {
      return carouselRef.value.select(0);
    }

    carouselRef.value.next();
  }, 5000);
});

onBeforeUnmount(() => {
  closeInterval();
});
</script>

<template>
  <UModal
    :model-value="shouldShow"
    prevent-close
    :ui="{
      width: modalWidth,
      height: modalHeight,
      container: modalContainer
    }"
  >
    <div class="announcements-modal">
      <button
        class="announcements-modal__close"
        type="button"
        aria-label="Close"
        @click="close"
      >
        <UIcon name="i-mdi-close" />
      </button>

      <UCarousel
        ref="carouselRef"
        :items="announcements"
        :indicators="announcements.length > 1"
        :ui="carouselUi"
        class="announcements-modal__carousel"
      >
        <template #default="{ item }">
          <div
            class="announcements-modal__card"
            :style="getCardStyle(item)"
          >
            <img
              v-if="item.imageUrl"
              :src="item.imageUrl"
              :alt="item.description"
              :title="item.description"
              class="announcements-modal__image"
            >

            <div
              v-else
              class="announcements-modal__fallback"
            >
              <span class="announcements-modal__title">
                {{ item.description }}
              </span>
            </div>
          </div>
        </template>
      </UCarousel>
    </div>
  </UModal>
</template>

<style scoped>
.announcements-modal {
  position: relative;
  padding: 0.5rem;

  &__close {
    position: absolute;
    top: 0.35rem;
    right: 0.35rem;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    border: none;
    background: #ffffffd9;
    color: #111827;
    cursor: pointer;
    z-index: 10;
  }

  &__carousel {
    padding: 1rem;
  }

  &__card {
    position: relative;
    border-radius: 1rem;
    min-height: 38.75rem;
    overflow: hidden;
    width: 100%;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    inset: 0;
  }

  &__fallback {
    padding: 2rem;
    height: 100%;
    align-items: center;
    display: flex;
    justify-content: center;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    &__card {
      min-height: 70vh;
    }

    &__title {
      font-size: 1.25rem;
    }
  }

}
</style>
